// src/components/pipeline/PipelineDeletedView.jsx
// The pipeline's Deleted tab: leads that were soft-deleted, newest first.
// Purely presentational — the pipeline PAGE fetches, filters and sorts, because
// the tab's count has to exist before this component is ever rendered.
//
// Deleted leads never expire. Nothing here happens on a timer; a lead is only
// destroyed when someone chooses to destroy it.
import React from "react";
import { RotateCcw, Trash2, Inbox } from "lucide-react";
import { formatDate } from "../../utils/helper";

const label = (l) => l?.propertyAddress || l?.contactName || "Untitled lead";

const secondary = (l) => {
  // Address is the headline for listings, so the client name is the detail.
  // Buyers lead with the name, so there is nothing left to repeat.
  const name = l?.propertyAddress ? l?.contactName : "";
  return [name, `Deleted ${formatDate(l?.deletedAt)}`].filter(Boolean).join(" · ");
};

export default function PipelineDeletedView({
  rows = [],
  canManage = () => false,
  onRestore,
  onPurge,
}) {
  if (!rows.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-400">
        <Inbox size={28} />
        <p className="text-sm mt-2">Nothing deleted.</p>
        <p className="text-xs mt-1">
          Deleted leads stay here until someone removes them.
        </p>
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {rows.map((lead) => (
        <li
          key={lead.id}
          className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4"
        >
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-900 truncate">
              {label(lead)}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">{secondary(lead)}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => onRestore && onRestore(lead)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <RotateCcw size={14} />
              Restore
            </button>
            {canManage(lead) && (
              <button
                type="button"
                onClick={() => onPurge && onPurge(lead)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash2 size={14} />
                Delete forever
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
